"use client";

import {toolRequestBody} from "./toolRequestBody";

import {useId, useRef, useState, type Ref} from "react";
import {useTranslation} from "react-i18next";
import {ApprovalStatusBanner, approvalVisualState} from "@repo/ui/gailgpt/approval-status-banner";
import {combinedApprovalStatus, type ToolApprovalStatus} from "@repo/ui/gailgpt/tool-approval-status";
import {displayToolPresentation} from "@repo/ui/gailgpt/tool-display";
import {useStagedApprovalDecisions} from "@/contexts/chat/ToolApprovalDecisionContext";
import type {ApprovalDecisionOutcome, ApprovalPart} from "@/types/chatMessages";
import type {ToolApprovalResponse} from "@/services/wingman";
import {approvalCanBeApplied} from "@/services/wingman/tool-approval";
import {Button} from "@/components/ui/button";
import Icon from "@/components/library/Icon";
import {approvalPresentation, approvalStatusKey} from "./approvalPresentation";
import UseCaseToolDetails from "./UseCaseToolDetails";

function trimBatchPrefix(prefix: string): string {
   let end = prefix.length;
   while (end > 0 && /[\s\-\u2013\u2014:]/.test(prefix[end - 1])) end--;
   return prefix.slice(0, end);
}

function batchChoice(choice: boolean | null) {
   if (choice === null) return "pending";
   return choice ? "approved" : "rejected";
}

function BatchDecisionButtons({pendingCount, total, blocked, canApproveRemaining, sending, submitRef, onDecideRemaining, onSubmit}: {
   pendingCount: number;
   total: number;
   blocked: boolean;
   canApproveRemaining: boolean;
   sending: boolean;
   submitRef: Ref<HTMLButtonElement>;
   onDecideRemaining: (approved: boolean) => void;
   onSubmit: () => Promise<void>;
}) {
   const {t} = useTranslation("ask");
   if (!pendingCount) return <Button ref={submitRef} type="button" className="approval-approve" disabled={blocked} onClick={() => void onSubmit()}>{t(sending ? "toolApproval.submitting" : "toolApproval.batch.submit")}</Button>;
   return <>
      <Button type="button" variant="outline" className="approval-reject" disabled={blocked} onClick={() => onDecideRemaining(false)}>{t(pendingCount === total ? "toolApproval.batch.rejectAll" : "toolApproval.rejectRemaining", {count: pendingCount})}</Button>
      <Button type="button" className="approval-approve" disabled={blocked || !canApproveRemaining} onClick={() => onDecideRemaining(true)}>{t(pendingCount === total ? "toolApproval.batch.approveAll" : "toolApproval.approveRemaining", {count: pendingCount})}</Button>
   </>;
}

/** Strip only a complete shared prefix ending at an explicit title separator. */
function batchNames(parts: ApprovalPart[]) {
   const names = parts.map(({data}) => {
      const body = toolRequestBody(data.parameters);
      return typeof body.title === "string" ? body.title : data.functionName;
   });
   const candidates = [...names[0].matchAll(/(?:\s[-\u2013\u2014]\s|:\s)/g)].map(match => names[0].slice(0, match.index + match[0].length)).reverse();
   const prefix = candidates.find(candidate => names.every(name => name.startsWith(candidate) && name.length > candidate.length)) ?? "";
   return {names: names.map(name => name.slice(prefix.length)), prefix: trimBatchPrefix(prefix)};
}

/** One review surface owns the batch; the existing reply handler submits all drafts together. */
export default function UseCaseApprovalBatch({parts, statuses, disabled, onRespond}: {
   parts: ApprovalPart[];
   statuses: ToolApprovalStatus[];
   disabled: boolean;
   onRespond?: (decisions: ToolApprovalResponse[]) => Promise<ApprovalDecisionOutcome>;
}) {
   const {t} = useTranslation("ask");
   const contentId = useId();
   const {state, stage} = useStagedApprovalDecisions(parts.map(part => part.data.approval.id));
   const [openId, setOpenId] = useState<string | null>(parts[0].data.approval.id);
   const [error, setError] = useState(false);
   const [sending, setSending] = useState(false);
   const busy = useRef(false);
   const headers = useRef(new Map<string, HTMLButtonElement>());
   const submitButton = useRef<HTMLButtonElement>(null);
   const status = combinedApprovalStatus(statuses, sending);
   const editable = statuses.map((value, index) => value === "required" || (value === "submitting" && parts[index].data.approval.approved === undefined));
   const pending = parts.filter((_, index) => editable[index] && state[index].draft === null);
   const unresolved = parts.filter((_, index) => editable[index]);
   const history = !editable.some(Boolean) && !sending;
   const blocked = disabled || sending || state.some(value => value.submitting) || !onRespond;
   const {names, prefix} = batchNames(parts);
   const choices = parts.map((part, index) => part.data.approval.approved ?? (editable[index] ? state[index].draft : null));
   const approved = choices.filter(choice => choice === true).length;
   const rejected = choices.filter(choice => choice === false).length;
   const canApprove = (part: ApprovalPart) => approvalCanBeApplied(part.data.approval, Date.now()) && !approvalPresentation(part.data).unavailable;
   const focusAfterDecision = (id: string | null) => requestAnimationFrame(() => (id ? headers.current.get(id) : submitButton.current)?.focus({preventScroll: true}));
   const decide = (index: number, approved: boolean | null) => {
      if (blocked || !editable[index] || (approved && !canApprove(parts[index]))) return;
      const id = parts[index].data.approval.id;
      stage([{id, approved}]);
      setError(false);
      const next = approved === null ? parts[index] : [...parts.slice(index + 1), ...parts.slice(0, index)].find(part => pending.includes(part));
      const nextId = next?.data.approval.id ?? null;
      setOpenId(nextId);
      focusAfterDecision(nextId);
   };
   const decideRemaining = (approved: boolean) => {
      if (blocked || (approved && !pending.every(canApprove))) return;
      stage(pending.map(part => ({id: part.data.approval.id, approved})));
      setError(false);
      setOpenId(null);
      focusAfterDecision(null);
   };
   const submit = async () => {
      if (busy.current || blocked || pending.length || !unresolved.length || !onRespond) return;
      const decisions = parts.flatMap((part, index) => editable[index] && state[index].draft !== null
         ? [{id: part.data.approval.id, approved: state[index].draft!}] : []);
      if (decisions.some(decision => decision.approved && !canApprove(parts.find(part => part.data.approval.id === decision.id)!))) {setError(true); return;}
      busy.current = true;
      setSending(true);
      setError(false);
      try {
         const outcome = await onRespond(decisions);
         if (outcome !== "submitted") setError(true);
      } catch {setError(true);} finally {busy.current = false; setSending(false);}
   };
   const rows = parts.map((part, index) => {
      const id = part.data.approval.id;
      const expanded = openId === id;
      const choice = choices[index];
      const choiceState = batchChoice(choice);
      const labelKey = editable[index] ? `toolApproval.batch.${choiceState}` : approvalStatusKey(part.data, statuses[index]);
      const label = t(labelKey);
      const {presentation} = approvalPresentation(part.data);
      const body = toolRequestBody(part.data.parameters);
      return <div key={id} className="approval-batch-row">
         <button ref={node => {if (node) headers.current.set(id, node); else headers.current.delete(id);}} type="button" className="approval-batch-toggle"
            aria-expanded={expanded} aria-controls={`${contentId}-${index}`} onClick={() => setOpenId(expanded ? null : id)}>
            <span className="approval-batch-index">{index + 1}</span>
            <span className="min-w-0 flex-1"><span className="block text-sm font-medium leading-5">{names[index]}</span>
               <span className="approval-meta block">{t("toolApproval.batch.newUseCase", {count: Array.isArray(body.actions) ? body.actions.length : 0})}</span></span>
            <span className="approval-batch-chip" data-choice={choiceState}>{label}</span>
            <Icon icon="chevron-down" className={`h-3 w-3 shrink-0 text-500 motion-safe:transition-transform motion-safe:duration-200 ${expanded ? "rotate-180" : ""}`} />
         </button>
         <div id={`${contentId}-${index}`} hidden={!expanded} className="approval-batch-body">
            {presentation && <UseCaseToolDetails presentation={displayToolPresentation(presentation, part.data.parameters, t)} parameters={part.data.parameters} batch />}
            {editable[index] && <div className="flex flex-wrap items-center justify-end gap-2">
               {choice === null ? <>
                  <Button type="button" variant="outline" className="approval-reject" disabled={blocked} onClick={() => decide(index, false)}>{t("toolApproval.batch.rejectThis")}</Button>
                  <Button type="button" className="approval-approve" disabled={blocked || !canApprove(part)} onClick={() => decide(index, true)}>{t("toolApproval.batch.approveThis")}</Button>
               </> : <><span className="text-approval-body text-600">{label}</span><Button type="button" variant="ghost" className="approval-open text-approval-body font-medium" disabled={blocked} onClick={() => decide(index, null)}>{t("toolApproval.batch.change")}</Button></>}
            </div>}
         </div>
      </div>;
   });
   return <section aria-label={t("toolApproval.batch.addUseCases", {count: parts.length})} className="approval-card approval-batch my-3 overflow-hidden rounded-xl border" data-approval-state={approvalVisualState(status)}>
      {history ? <details className="approval-batch-receipt group/receipt"><summary data-approval-state={approvalVisualState(status)} className="flex cursor-pointer list-none items-start gap-2.5 px-4 py-3 [&::-webkit-details-marker]:hidden">
         <span className="min-w-0 flex-1"><span className="text-sm font-medium">{t("toolApproval.batch.summary", {approved, rejected})}</span>
            {prefix && <span className="approval-meta block">{prefix}</span>}
            <span className="approval-meta block">{t(approvalStatusKey(parts[0].data, status))} · {t("toolApproval.decidedCount", {decided: approved + rejected, total: parts.length})}</span></span>
         <Icon icon="chevron-down" className="mt-1 h-3 w-3 shrink-0 group-open/receipt:rotate-180" />
      </summary>{rows}</details> : <>
         <ApprovalStatusBanner state={approvalVisualState(status)}>{t(status === "required" ? "toolApproval.waiting" : `toolApproval.${status}`)}</ApprovalStatusBanner>
         <div className="approval-heading space-y-2.5">
            <div><h3 className="approval-title">{t("toolApproval.batch.addUseCases", {count: parts.length})}</h3>{prefix && <p className="approval-meta">{prefix}</p>}</div>
            <div className="flex items-center gap-2.5">
               <div aria-hidden="true" className="flex min-w-0 flex-1 gap-1">{choices.map((choice, index) => <span key={parts[index].data.approval.id} className="approval-batch-segment" data-choice={batchChoice(choice)} />)}</div>
               <output aria-live="polite" className="shrink-0 text-xs font-medium text-700">{t("toolApproval.decidedCount", {decided: approved + rejected, total: parts.length})}</output>
            </div>
         </div>
         {rows}
         {!!unresolved.length && <footer className="space-y-2.5 border-t px-4 py-3">
            <p className="text-xs leading-approval-meta text-600">{t(pending.length ? "toolApproval.batch.reviewBeforeSubmit" : "toolApproval.batch.readyToSubmit", {approved, rejected})}</p>
            {error && <p role="alert" className="text-xs text-red-700">{t("toolApproval.submitError")}</p>}
            <div className="flex flex-wrap justify-end gap-2">
               <BatchDecisionButtons pendingCount={pending.length} total={parts.length} blocked={blocked} canApproveRemaining={pending.every(canApprove)}
                  sending={sending} submitRef={submitButton} onDecideRemaining={decideRemaining} onSubmit={submit} />
            </div>
         </footer>}
      </>}
   </section>;
}
